<script setup lang="ts">
// Ava's profile header as the real DefaultLayout renders it (avatar, display
// name with achievement badges, location, separator, bio, icon links), for use
// inside a .mock-card. Real sizes; scale the card with `zoom` where it is small.
withDefaults(defineProps<{
  /** Show location, bio and links under the name */
  full?: boolean
  /** Avatar size in px (real default 96) */
  avatarSize?: number
  /** Font class for name, location and bio (the page font) */
  fontClass?: string
}>(), { full: true, avatarSize: 96, fontClass: 'font-space-grotesk' })

const badges = [
  { icon: 'i-lucide-crown', color: '#fbbf24' },
  { icon: 'i-lucide-gem', color: '#e879f9' },
  { icon: 'i-simple-icons-discord', color: '#5865F2' },
  { icon: 'i-lucide-heart', color: '#fb7185' }
]

// Platform links in the default icon style; null colour = page text colour
// (white brand icons would vanish on the light page theme)
const links = [
  { icon: 'i-simple-icons-youtube', color: '#FF0000' },
  { icon: 'i-simple-icons-tiktok', color: null },
  { icon: 'i-simple-icons-instagram', color: '#E4405F' },
  { icon: 'i-simple-icons-twitch', color: '#9146FF' },
  { icon: 'i-simple-icons-spotify', color: '#1DB954' }
]
</script>

<template>
  <div class="text-center">
    <div class="mb-6 flex justify-center">
      <span
        class="relative block overflow-hidden rounded-full ring-2 ring-primary-400/30 drop-shadow-[0_0_12px_rgba(var(--mock-accent-rgb),0.25)]"
        :style="{ width: `${avatarSize}px`, height: `${avatarSize}px` }"
      >
        <MockPhoto
          name="avatar-ava"
          single
        />
      </span>
    </div>

    <div class="mb-1 flex flex-wrap items-center justify-center gap-2">
      <h1
        class="text-[2.5rem] font-medium leading-[1.2] text-(--mock-text-strong)"
        :class="fontClass"
      >
        Ava
      </h1>
      <div class="flex items-center gap-0.5">
        <span
          v-for="badge in badges"
          :key="badge.icon"
          class="flex items-center justify-center rounded-full bg-primary-500/10 px-1.5 py-1"
        >
          <UIcon
            :name="badge.icon"
            class="size-4"
            :style="{ color: badge.color }"
          />
        </span>
      </div>
    </div>

    <template v-if="full">
      <div
        class="mb-6 mt-3 flex items-center justify-center gap-1.5 text-sm text-(--mock-text-dim)"
        :class="fontClass"
      >
        <UIcon
          name="i-lucide-map-pin"
          class="size-3.5 shrink-0"
        />
        Lisbon
      </div>

      <div class="mx-auto mb-6 mt-4 h-px max-w-xs bg-[rgba(var(--mock-accent-rgb),0.3)]" />

      <p
        class="mx-auto max-w-md text-lg text-(--mock-text-muted)"
        :class="fontClass"
      >
        Songwriter and streamer. New EP out now.
      </p>

      <div class="mt-6 flex flex-wrap items-center justify-center gap-2">
        <span
          v-for="link in links"
          :key="link.icon"
          class="flex size-10 items-center justify-center rounded-xl"
        >
          <UIcon
            :name="link.icon"
            class="size-5"
            :class="link.color ? '' : 'text-(--mock-text-strong)'"
            :style="link.color ? { color: link.color } : undefined"
          />
        </span>
      </div>
    </template>
  </div>
</template>
